import React, { useState } from 'react';
import { DSBackLink, DSIcon, DSInput, DSTextarea } from '../design-system/primitives';
import './ReportIssueIntake.css';

// Ported from the Machine Ticket Tiering wireframe (Mobile UI for Ticket
// Tiering / App.tsx, ReportTicket component), extended with a duplicate-check
// step: when the machine has existing Known Issues or open tickets, they're
// shown first so the reporter can confirm theirs isn't already logged (with
// an optional +1 on a matching ticket) before continuing to the intake
// questions. Steps: 0 (duplicate check, only when there's something to show)
// -> 1 ("is it unplayable") -> 2 (tournament-play follow-up, only if step 1
// was No) -> 3 (description + name + submit) -> success screen.
//
// Reporter name is intentionally required (unlike the wireframe's "optional,
// leave blank to report anonymously" version) - every report still needs an
// attributable name, matching current backend validation. It's asked in step
// 3, and only when the caller hasn't already supplied a read-only prefilled
// name (i.e. the reporter isn't logged in).

const IMPACT_CONFIG = {
  game_down: { label: 'Game Down', bg: '#2a0e0e', border: '#7f1d1d', text: '#ff6b6b', dot: '#ff3b3b' },
  tournament_risk: { label: 'Tournament Risk', bg: '#271c06', border: '#7c4a00', text: '#ffb020', dot: '#ffb020' },
  observation: { label: 'Observation', bg: '#181b2a', border: '#2d3148', text: '#7b82a6', dot: '#4a5070' }
};

function deriveImpact(gameUnplayable, affectsTournamentPlay) {
  if (gameUnplayable === true) return 'game_down';
  if (gameUnplayable === false && affectsTournamentPlay === true) return 'tournament_risk';
  if (gameUnplayable === false && affectsTournamentPlay === false) return 'observation';
  return null;
}

function ImpactBadge({ impact, size = 'sm' }) {
  const cfg = IMPACT_CONFIG[impact];
  if (!cfg) return null;
  return (
    <span
      className={`rii__badge rii__font-display ${size === 'md' ? 'rii__badge--md' : ''}`}
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, color: cfg.text }}
    >
      <span className="rii__badge-dot" style={{ background: cfg.dot }} />
      {cfg.label}
    </span>
  );
}

function ReportIssueIntake({
  reporterName,
  onReporterNameChange,
  reporterNameReadOnly = false,
  machineName,
  knownIssues = [],
  openIssues = [],
  onUpvote,
  submitting = false,
  error = '',
  success = false,
  onSubmit,
  onDone
}) {
  const hasContext = knownIssues.length + openIssues.length > 0;
  const [step, setStep] = useState(hasContext ? 0 : 1);
  const [gameUnplayable, setGameUnplayable] = useState(null);
  const [affectsTournamentPlay, setAffectsTournamentPlay] = useState(null);
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState('');

  const impact = deriveImpact(gameUnplayable, affectsTournamentPlay);

  const handleQ1 = (val) => {
    setGameUnplayable(val);
    setAffectsTournamentPlay(null);
    setStep(val ? 3 : 2);
  };

  const handleQ2 = (val) => {
    setAffectsTournamentPlay(val);
    setStep(3);
  };

  const handleSubmit = () => {
    setValidationError('');
    if (!reporterNameReadOnly && (!reporterName || !reporterName.trim())) {
      setValidationError('Please provide your name.');
      return;
    }
    if (!description.trim()) {
      setValidationError('Please describe the issue.');
      return;
    }
    onSubmit({
      description: description.trim(),
      gameUnplayable,
      affectsTournamentPlay: gameUnplayable ? undefined : affectsTournamentPlay
    });
  };

  if (success) {
    return (
      <div className="rii">
        <div className="rii__success">
          <div className="rii__success-icon"><DSIcon name="check" /></div>
          <p className="rii__success-title rii__font-display">Report Submitted</p>
          <p className="rii__success-text">
            Your report{machineName ? <> on <strong>{machineName}</strong></> : ''} has been logged. A tech will review it.
          </p>
          {impact && (
            <div className="rii__success-badge">
              <ImpactBadge impact={impact} size="md" />
            </div>
          )}
          <button type="button" className="rii__done-btn rii__font-display" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    );
  }

  const dots = hasContext ? [0, 1, 2, 3] : [1, 2, 3];

  return (
    <div className="rii">
      <div className="rii__steps">
        {dots.map((s) => (
          <span
            key={s}
            className={`rii__step-dot ${step >= s ? 'rii__step-dot--active' : ''}`}
          />
        ))}
      </div>

      {step === 0 && (
        <>
          <p className="rii__question-title rii__font-display">Does this match something already reported?</p>
          <p className="rii__question-subtext">
            Take a look before filing a new report. If one of these is your issue, there's no need to report it again.
          </p>

          {knownIssues.length > 0 && (
            <div className="rii__context-section">
              <p className="rii__context-subheading rii__font-display">Known Issues</p>
              {knownIssues.map((ki) => (
                <div key={ki.id} className="rii__ki-card">
                  <p className="rii__ki-title rii__font-display">{ki.description}</p>
                  {ki.guidance_text && <p className="rii__ki-guidance">{ki.guidance_text}</p>}
                </div>
              ))}
            </div>
          )}

          {openIssues.length > 0 && (
            <div className="rii__context-section">
              <p className="rii__context-subheading rii__font-display">Open Tickets</p>
              {openIssues.map((issue) => (
                <div key={issue.id} className="rii__ticket-card">
                  <div className="rii__ticket-card-top">
                    {issue.impact && <ImpactBadge impact={issue.impact} />}
                    <p className="rii__ticket-desc">{issue.description}</p>
                  </div>
                  {onUpvote ? (
                    <button
                      type="button"
                      className={`rii__upvote-btn ${issue.user_has_upvoted ? 'rii__upvote-btn--active' : ''} rii__font-mono`}
                      onClick={() => onUpvote(issue.id)}
                    >
                      {issue.user_has_upvoted ? <><DSIcon name="check" /> Noted (this is happening to me too)</> : '+1 This is happening to me too'}
                      {typeof issue.upvote_count === 'number' && issue.upvote_count > 0 && (
                        <span className="rii__upvote-count">{issue.upvote_count}</span>
                      )}
                    </button>
                  ) : (
                    typeof issue.upvote_count === 'number' && issue.upvote_count > 0 && (
                      <p className="rii__upvote-readonly rii__font-mono">
                        {issue.upvote_count} other{issue.upvote_count !== 1 ? 's' : ''} reported this too
                      </p>
                    )
                  )}
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="rii__submit-btn rii__font-display"
            onClick={() => setStep(1)}
          >
            None of these - Report a New Issue
          </button>
        </>
      )}

      {step === 1 && (
        <>
          {hasContext && (
            <DSBackLink tone="subtle" size="sm" onClick={() => setStep(0)}>Back</DSBackLink>
          )}
          <p className="rii__question-title rii__font-display">Is the game currently unplayable?</p>
          <div className="rii__question-help">
            <strong>Answer yes if:</strong> there's a safety concern, the game won't power on or start a ball, or
            continuing to play would cause further damage. <strong>Minor issues</strong> (a burnt-out bulb, a worn
            rubber, a loose lockdown bar) are usually "no."
          </div>
          <div className="rii__answers">
            <button type="button" className="rii__big-btn rii__big-btn--danger" onClick={() => handleQ1(true)}>
              Yes, game is unplayable
            </button>
            <button type="button" className="rii__big-btn rii__big-btn--neutral" onClick={() => handleQ1(false)}>
              No, game can still be played
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <DSBackLink tone="subtle" size="sm" onClick={() => setStep(1)}>Back</DSBackLink>
          <p className="rii__question-title rii__font-display">Could this affect tournament play?</p>
          <div className="rii__question-help">
            <strong>Ask yourself:</strong> does this affect all players equally? An intermittent fault, like a
            skill shot that only sometimes works, is unfair since it could help one player and hurt another who
            played just as well. A fault that never works for anyone is just annoying, not unfair.
          </div>
          <div className="rii__answers">
            <button type="button" className="rii__big-btn rii__big-btn--caution" onClick={() => handleQ2(true)}>
              Yes, could affect a tournament game
            </button>
            <button type="button" className="rii__big-btn rii__big-btn--neutral" onClick={() => handleQ2(false)}>
              No, just a minor observation
            </button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <DSBackLink tone="subtle" size="sm" onClick={() => setStep(gameUnplayable ? 1 : 2)}>Back</DSBackLink>

          {impact && (
            <div className="rii__impact-preview">
              <ImpactBadge impact={impact} size="md" />
              <p className="rii__impact-preview-text">This report will be filed as {IMPACT_CONFIG[impact].label}</p>
            </div>
          )}

          <p className="rii__field-title rii__font-display">Describe the problem</p>
          <p className="rii__field-subtext">Be specific - what exactly are you seeing?</p>
          <DSTextarea
            className="rii__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Left flipper not responding, machine makes a clunk sound and resets…"
            disabled={submitting}
          />

          {!reporterNameReadOnly && (
            <div className="rii__field-group">
              <label className="rii__field-label rii__font-display">Your Name</label>
              <DSInput
                type="text"
                className="rii__input"
                value={reporterName}
                onChange={(e) => onReporterNameChange && onReporterNameChange(e.target.value)}
                placeholder="Enter your name"
                disabled={submitting}
              />
            </div>
          )}

          {(validationError || error) && <div className="rii__error">{validationError || error}</div>}

          <button
            type="button"
            className="rii__submit-btn rii__font-display"
            onClick={handleSubmit}
            disabled={submitting || !description.trim()}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </>
      )}
    </div>
  );
}

export default ReportIssueIntake;
